<template>
  <div class="dashboard-health-trend">
    <div class="ht-header">
      <h3 class="ht-title">{{ $t('org-dashboard.health-trend-title') }}</h3>
      <span class="ht-subtitle">{{ $t('org-dashboard.health-trend-subtitle') }}</span>
    </div>

    <div v-if="data && data.length" class="ht-chart">
      <highcharts :options="chartOptions" />
    </div>
    <div v-else class="ht-empty">
      <v-icon color="#d1d5db" size="32">mdi-chart-timeline-variant</v-icon>
      <span>{{ $t('org-dashboard.health-no-data') }}</span>
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardHealthTrend',
  components: { highcharts: Chart },

  props: {
    data: { type: Array, default: () => [] },
  },

  computed: {
    chartOptions() {
      if (!this.data?.length) return null

      const availData = this.data.map(d => [new Date(d.date).getTime(), d.operationalAvailability ?? 0])
      const incidentData = this.data.map(d => [new Date(d.date).getTime(), (d.incidents ?? 0) + (d.criticals ?? 0)])

      return {
        chart: { type: 'areaspline', height: 320, backgroundColor: 'transparent', spacing: [8, 0, 8, 0] },
        title: { text: '' },
        credits: { enabled: false },
        legend: {
          enabled: true,
          align: 'right',
          verticalAlign: 'top',
          floating: true,
          itemStyle: { fontSize: '11px', fontWeight: '500', color: '#6b7280' },
        },
        xAxis: {
          type: 'datetime',
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineColor: '#e5e7eb',
          tickColor: '#e5e7eb',
        },
        yAxis: [
          {
            title: { text: '' },
            labels: { format: '{value}%', style: { fontSize: '10px', color: '#9ca3af' } },
            max: 100,
            min: 0,
            gridLineColor: '#f3f4f6',
            plotBands: [
              { from: 0, to: 80, color: 'rgba(239,68,68,0.04)' },
              { from: 80, to: 95, color: 'rgba(234,179,8,0.04)' },
              { from: 95, to: 100, color: 'rgba(34,197,94,0.04)' },
            ],
          },
          {
            title: { text: '' },
            labels: { style: { fontSize: '10px', color: '#9ca3af' } },
            opposite: true,
            min: 0,
            gridLineWidth: 0,
          },
        ],
        tooltip: {
          shared: true,
          backgroundColor: 'white',
          borderColor: '#e5e7eb',
          borderRadius: 8,
          shadow: { color: 'rgba(0,0,0,0.06)', offsetX: 0, offsetY: 2, width: 8 },
          style: { fontSize: '11px' },
        },
        series: [
          {
            name: this.$t('org-dashboard.health-availability-label'),
            data: availData,
            color: '#22c55e',
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [[0, 'rgba(34,197,94,0.2)'], [1, 'rgba(34,197,94,0.02)']],
            },
            yAxis: 0,
            tooltip: { valueSuffix: '%' },
            marker: { radius: 2 },
          },
          {
            name: this.$t('org-dashboard.health-incident'),
            data: incidentData,
            type: 'column',
            color: '#f97316',
            yAxis: 1,
            borderRadius: 3,
            pointWidth: 6,
          },
        ],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-health-trend {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.ht-chart {
  flex: 1;
}

.ht-header {
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.ht-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.ht-subtitle {
  font-size: 11px;
  color: #9ca3af;
}

.ht-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;

  span {
    font-size: 12px;
    color: #9ca3af;
  }
}
</style>
